import React,{ useState, useEffect, useCallback } from "react";
import { reqVapidPublicKey, reqRegisterPushSubscription, reqRemovePushSubscription } from 'ajax';

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = atob(base64);
  const array = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    array[i] = rawData.charCodeAt(i);
  }
  return array;
}

export const usePushNotifications = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [error, setError] = useState(null);
  
  // On mount: optionally check existing subscription
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("Push or service workers not supported in this browser");
      return;
    }

    const savedId = localStorage.getItem("pushSubscriptionId");
    if (savedId && savedId!=="null") {
      setSubscriptionId(savedId);
      setEnabled(true);
    }

    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (!sub) {
            // subscription has been removed – clear state
            localStorage.removeItem("pushSubscriptionId");
            setSubscriptionId(null);
            setEnabled(false);
          }
        }
      } catch (err) {
        console.error("Error in init check existing subscription:", err);
      }
    })();
  }, []);

  const enable = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (typeof window === "undefined") {
        throw new Error("Notifications must be enabled from the browser");
      }

      if (!("Notification" in window)) {
        throw new Error("This browser does not support notifications");
      }

      if (!("serviceWorker" in navigator)) {
        throw new Error("Service workers are not supported in this browser");
      }

      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        throw new Error("Notification permission denied");
      }

      const reg = await navigator.serviceWorker.register(`/sw.js`);
      if (!reg) {
        throw new Error("Service worker registration failed");
      }

      // Fetch VAPID public key
      const resp = await reqVapidPublicKey();
      if (resp.error) {
        const txt = resp.error;
        throw new Error("Failed to fetch VAPID public key: " + txt);
      }
      const publicKey = resp.publicKey;
      if (!publicKey) {
        throw new Error("No publicKey in response");
      }

      const swReg = await navigator.serviceWorker.ready;
      if (!swReg) {
        throw new Error("Service worker registration failed");
      }
      const sub = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      if (!sub) {
        throw new Error("Push Manager subscription failed");
      }

      const body = {
        endpoint: sub.endpoint,
        keys: sub.toJSON().keys,
        clientName: navigator.userAgent
      };

      const registerResp = await reqRegisterPushSubscription(JSON.stringify(body));
      if (registerResp.error) {
        const txt = registerResp.error
        throw new Error("Failed to register subscription at backend: " + txt);
      }

      // Expect backend to return subscription record with id
      const newId = (registerResp.subscription && registerResp.subscription.id) || null;
      if (newId) {
        localStorage.setItem("pushSubscriptionId", newId);
        setSubscriptionId(newId);
        setEnabled(true);
      }
      if (!newId) {
        console.warn("Backend did not return subscription id");
      }
    } catch (err) {
      console.error("Error enabling push notifications:", err);
      setError(err.message || String(err));
      throw err;
    }
    setLoading(false);
  }, []);

  const disable = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!subscriptionId) {
        throw new Error("No subscription ID to delete");
      }

      // Delete from backend
      const resp = await reqRemovePushSubscription(subscriptionId);

      if (resp.error) {
        const txt = resp.error
        throw new Error("Failed to delete subscription on backend: " + txt);
      }

      // Also unsubscribe in service worker
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await sub.unsubscribe();
        }
      }
      localStorage.removeItem("pushSubscriptionId");
      setEnabled(false);
      setSubscriptionId(null);
    } catch (err) {
      console.error("Error disabling push notifications:", err);
      setError(err.message || String(err));
      throw err;
    }
    setLoading(false);
  }, [subscriptionId]);

  return {
    enabled,
    loading,
    subscriptionId,
    error,
    enable,
    disable
  };
}